import React from 'react';
import EditorCardHeader from './EditorCardHeader';
import EditorFormattingToolbar from './EditorFormattingToolbar';
import EditorCardFooter from './EditorCardFooter';
import { stripPrefix } from '../../team-lead/analysis/utils/helperUtils';
import { exportQueriesToExcel } from '../../services/rfpService';
import UnifiedTiptapEditor from './UnifiedTiptapEditor';
import { Download } from 'lucide-react';
import { markdownToHTML } from '../../team-lead/analysis/utils/markdownUtils';
import { CommercialsSectionButton, RaciLegend, AutoSaveBanner } from './components/ContentEditorPanels';
import './ContentEditor.css';

// ─── HTML/Markdown content formatter ─────────────────────────────────────────
const formatContent = (content) => {
    if (!content || typeof content !== 'string') return '';
    if (/<[a-z][\s\S]*>/i.test(content)) {
        let processed = content;
        processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        processed = processed.replace(/(?<!\*)\*(?!\*)([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
        processed = processed.replace(/^(#{1,6})\s+(.+)$/gm, (_, hashes, title) => {
            const level = Math.min(hashes.length + 1, 4);
            return `<h${level}>${title}</h${level}>`;
        });
        return processed;
    }
    if (content.includes('|')) {
        const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
        const pipeLines = lines.filter(line => line.startsWith('|') && line.split('|').length > 2);
        if (pipeLines.length > 0) return markdownToHTML(pipeLines.join('\n'));
    }
    return markdownToHTML(content);
};

/**
 * ContentEditor — Phase 3 (Final Cleanup & Sync).
 * - All editing now goes through UnifiedTiptapEditor (Tiptap).
 * - Undo/Redo wired to Tiptap's native history.
 * - handleSidePanelEdit feeds updated markdown to server on "Update".
 * - Dead block-based code removed.
 */
const ContentEditor = ({
    activeSection,
    selectedSection,
    selectedCell,
    editableData,
    isEditingContent,
    isUpdating,
    handleUpdate,
    startEditing,
    setShowAIModal,
    handleSidePanelEdit,
    cancelEditing,
    renderKeyInformation,
    isManager,
    setStageModal,
    setStageError,
    handleToggleSectionApproval,
    handleSubmitToLead,
    isSubmittingToLead,
    isApprovingSection = false,
    setShowVersionHistory,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    assignments = {},
    rfpId,
    fetchRFPData,
    isNoGo = false,
    isTeamLeadViewOnly = false,
    // ── Collaboration props (all optional) ──────────────────────────
    collabYdoc = null,
    collabProvider = null,
    collabUser = null,
    collabStatus = null,
    collaborators = [],
    collabReady = false,
    // ── Global sync ──────────────────────────────────────
    onCancelSync = null,
}) => {
    const sectionTitle = selectedSection?.label || selectedSection?.key || stripPrefix(activeSection);
    const tiptapRef = React.useRef(null);

    const [isExporting, setIsExporting] = React.useState(false);
    const [isTiptapTableActive, setIsTiptapTableActive] = React.useState(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);

    // Unsaved changes warning logic
    const initialContentRef = React.useRef('');
    React.useEffect(() => {
        if (isEditingContent) {
            initialContentRef.current = formatContent(editableData[activeSection] || '');
        }
    }, [isEditingContent, activeSection, editableData]);

    const handleCancelWithWarning = async () => {
        const currentContent = editableData[activeSection] || '';
        if (currentContent !== initialContentRef.current) {
            if (window.confirm('You have unsaved changes in your RFP response draft. Are you sure you want to discard them?')) {
                await cancelEditing();
            }
        } else {
            await cancelEditing();
        }
    };

    // Subscribe to Tiptap selection so toolbar enables/disables table buttons
    React.useEffect(() => {
        const editor = tiptapRef.current;
        if (!editor) return;
        const handler = () => setIsTiptapTableActive(editor.isActive('table'));
        editor.on('selectionUpdate', handler);
        editor.on('transaction', handler);
        return () => { editor.off('selectionUpdate', handler); editor.off('transaction', handler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiptapRef.current]);

    // ── Format / Table actions via Tiptap native commands ──────────────────
    const handleFormatAction = (action, payload = null) => {
        if (isTeamLeadViewOnly) return;
        const editor = tiptapRef.current;
        if (!editor) return;

        const setListStyle = (type, attrName, attrValue) => {
            if (editor.isActive('listItem')) {
                if (type === 'bulletList') editor.chain().focus().liftListItem('listItem').toggleBulletList().updateAttributes(type, { [attrName]: attrValue }).run();
                else editor.chain().focus().liftListItem('listItem').toggleOrderedList().updateAttributes(type, { [attrName]: attrValue }).run();
            } else {
                if (type === 'bulletList') editor.chain().focus().toggleBulletList().updateAttributes(type, { [attrName]: attrValue }).run();
                else editor.chain().focus().toggleOrderedList().updateAttributes(type, { [attrName]: attrValue }).run();
            }
        };

        if (action === 'insertTable') { const { rows, cols } = payload || { rows: 3, cols: 3 }; editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run(); return; }
        if (action.startsWith('OrderedList:')) { setListStyle('orderedList', 'type', action.split(':')[1]); return; }
        if (action.startsWith('BulletList:')) {
            const styleMap = { disc: 'disc', circle: 'circle', square: 'square', dash: "'-'", arrow: "'➜'", diamond: "'◆'" };
            setListStyle('bulletList', 'style', styleMap[action.split(':')[1]] || 'disc');
            return;
        }

        switch (action) {
            case 'Bold': editor.chain().focus().toggleBold().run(); break;
            case 'Italic': editor.chain().focus().toggleItalic().run(); break;
            case 'Underline': editor.chain().focus().toggleUnderline().run(); break;
            case 'Strike': editor.chain().focus().toggleStrike().run(); break;
            case 'paragraph': editor.chain().focus().setParagraph().run(); break;
            case 'heading1': editor.chain().focus().toggleHeading({ level: 1 }).run(); break;
            case 'heading2': editor.chain().focus().toggleHeading({ level: 2 }).run(); break;
            case 'heading3': editor.chain().focus().toggleHeading({ level: 3 }).run(); break;
            case 'Bullet List': editor.isActive('orderedList') ? editor.chain().focus().liftListItem('listItem').toggleBulletList().run() : editor.chain().focus().toggleBulletList().run(); break;
            case 'Numbered List': editor.isActive('bulletList') ? editor.chain().focus().liftListItem('listItem').toggleOrderedList().run() : editor.chain().focus().toggleOrderedList().run(); break;
            case 'Add Paragraph': editor.chain().focus().setParagraph().run(); break;
            case 'addRow': editor.chain().focus().addRowAfter().run(); break;
            case 'addColumn': editor.chain().focus().addColumnAfter().run(); break;
            case 'deleteRow': editor.chain().focus().deleteRow().run(); break;
            case 'deleteColumn': editor.chain().focus().deleteColumn().run(); break;
            default: break;
        }
    };

    const handleUndo = () => tiptapRef.current?.chain().focus().undo().run();
    const handleRedo = () => tiptapRef.current?.chain().focus().redo().run();

    const handleExport = async () => {
        if (!rfpId || isExporting) return;
        try {
            setIsExporting(true);
            const response = await exportQueriesToExcel(rfpId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Clarifications_Queries_${rfpId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const isCommercialsSection = activeSection === 'chapter_commercials' || activeSection?.toLowerCase() === 'commercials' || selectedSection?.key === 'chapter_commercials';
    const isRaciSection = (activeSection || '').toUpperCase().includes('RACI') || (sectionTitle || '').toUpperCase().includes('RACI');
    const isQueriesSection = isManager && (activeSection?.toUpperCase().includes('CLARIFICATIONS') || activeSection?.toUpperCase().includes('QUERIES'));

    return (
        <div className="final-main-content">
            <div className={`final-content-card ${isFullScreen ? 'fullscreen-active' : ''}`}>
                <EditorCardHeader
                    sectionTitle={sectionTitle}
                    isSidebarCollapsed={isSidebarCollapsed}
                    setIsSidebarCollapsed={setIsSidebarCollapsed}
                    editableData={editableData}
                    isEditingContent={isEditingContent}
                    isUpdating={isUpdating}
                    isManager={isManager}
                    handleUpdate={handleUpdate}
                    startEditing={startEditing}
                    cancelEditing={handleCancelWithWarning}
                    setShowAIModal={setShowAIModal}
                    setShowVersionHistory={setShowVersionHistory}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                    assignments={assignments}
                    selectedSection={selectedSection}
                    onCancelSync={onCancelSync}
                />

                {isEditingContent && (
                    <EditorFormattingToolbar
                        editor={tiptapRef.current}
                        editorRef={tiptapRef}
                        isTableActive={isTiptapTableActive}
                        handleFormatAction={handleFormatAction}
                        undo={handleUndo}
                        redo={handleRedo}
                        rfpId={rfpId}
                        isFullScreen={isFullScreen}
                        setIsFullScreen={setIsFullScreen}
                    />
                )}

                <div className={`final-card-body ${isEditingContent ? 'editing-mode' : ''}`}>
                    {activeSection === 'Key Information' ? (
                        renderKeyInformation()
                    ) : (
                        <>
                            {isEditingContent ? (
                                <div className="unified-editor-container">
                                    {(collabStatus === 'connecting' && !collabYdoc) ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4rem 2rem', color: '#64748B', fontSize: '14px', fontStyle: 'italic' }}>
                                            Initializing collaborative editor...
                                        </div>
                                    ) : (
                                        <UnifiedTiptapEditor
                                            key={collabYdoc && collabStatus !== 'unavailable' ? `collab-${activeSection}` : `standard-${activeSection}`}
                                            htmlContent={formatContent(editableData[activeSection] || '')}
                                            onChange={(html) => { if (typeof handleSidePanelEdit === 'function') handleSidePanelEdit(html); }}
                                            editorRef={tiptapRef}
                                            isFullScreen={isFullScreen}
                                            rfpId={rfpId}
                                            collabYdoc={collabYdoc}
                                            collabProvider={collabProvider}
                                            collabUser={collabUser}
                                            collabStatus={collabStatus}
                                            collaborators={collaborators}
                                            collabReady={collabReady}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="final-view-area" style={{ position: 'relative' }}>
                                    {isQueriesSection && (
                                        <button className={`section-export-inline-btn ${isExporting ? 'exporting' : ''}`} onClick={handleExport} disabled={isExporting} title="Export section data to Excel">
                                            <Download size={14} className={isExporting ? 'animate-bounce' : ''} />
                                            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                                        </button>
                                    )}
                                    {isCommercialsSection && !isNoGo && (
                                        <CommercialsSectionButton rfpId={rfpId} editableData={editableData} fetchRFPData={fetchRFPData} />
                                    )}
                                    <div className="content-padding-redesign seamless-tiptap-content">
                                        <div dangerouslySetInnerHTML={{ __html: formatContent(editableData[activeSection] || '') }} />
                                    </div>
                                </div>
                            )}
                            {isRaciSection && <RaciLegend />}
                        </>
                    )}
                </div>

                {isEditingContent && <AutoSaveBanner />}

                <div className="final-card-footer">
                    <EditorCardFooter
                        editableData={editableData}
                        activeSection={activeSection}
                        selectedSection={selectedSection}
                        sectionTitle={sectionTitle}
                        assignments={assignments}
                        isManager={isManager}
                        handleUpdate={handleUpdate}
                        handleSubmitToLead={handleSubmitToLead}
                        isSubmittingToLead={isSubmittingToLead}
                        handleToggleSectionApproval={handleToggleSectionApproval}
                        isApprovingSection={isApprovingSection}
                        setStageModal={setStageModal}
                        setStageError={setStageError}
                        isNoGo={isNoGo}
                        isTeamLeadViewOnly={isTeamLeadViewOnly}
                    />
                </div>
            </div>
        </div>
    );
};

export default ContentEditor;
